import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function useABTest(testName: string) {
  const [variant, setVariant] = useState<string | null>(null);
  const [variantConfig, setVariantConfig] = useState<Record<string, any> | null>(null);
  const [sessionId] = useState(() => {
    const stored = sessionStorage.getItem(`ab_session_${testName}`);
    if (stored) return stored;
    const id = crypto.randomUUID();
    sessionStorage.setItem(`ab_session_${testName}`, id);
    return id;
  });

  const { data: test } = useQuery({
    queryKey: ["ab-test", testName],
    queryFn: async () => {
      const { data } = await supabase
        .from("ab_tests")
        .select("*")
        .eq("test_name", testName)
        .eq("is_active", true)
        .single();
      return data;
    },
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!test || variant) return;
    const allVariants = (test.variants as any[]) || [];
    const activeVariants = allVariants.filter((v: any) => v.active !== false);
    if (activeVariants.length === 0) return;

    // Check if already assigned
    const stored = localStorage.getItem(`ab_variant_${testName}`);
    if (stored && activeVariants.some((v: any) => (v.key || v) === stored)) {
      setVariant(stored);
      const cfg = activeVariants.find((v: any) => (v.key || v) === stored);
      if (cfg) setVariantConfig(cfg);
      return;
    }

    // Weighted random assignment from active variants
    const totalWeight = activeVariants.reduce((s: number, v: any) => s + (v.weight ?? Math.round(100 / activeVariants.length)), 0);
    const rand = Math.random() * totalWeight;
    let cumulative = 0;
    let chosen = activeVariants[0];
    for (const v of activeVariants) {
      cumulative += (v.weight ?? Math.round(100 / activeVariants.length));
      if (rand < cumulative) {
        chosen = v;
        break;
      }
    }
    const key = chosen.key || chosen;
    localStorage.setItem(`ab_variant_${testName}`, key);
    setVariant(key);
    setVariantConfig(chosen);
  }, [test, testName, variant]);

  const trackEvent = async (eventType: string) => {
    if (!variant) return;
    const payload = {
      test_name: testName,
      variant,
      event_type: eventType,
      session_id: sessionId,
      user_agent: navigator.userAgent,
    };

    // Send via edge function so the server can detect the IP and apply
    // the IP exclusion list. Use sendBeacon for click events to survive
    // page navigation/unload.
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-ab-event`;
    const apiKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    if (eventType === "click" && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        // sendBeacon cannot set custom headers, so include the apikey in the body.
        // The edge function ignores it; Supabase gateway accepts the request because
        // it does not require JWT verification for this function.
        const body = new Blob([JSON.stringify({ ...payload, apikey: apiKey })], {
          type: "application/json",
        });
        const sent = navigator.sendBeacon(`${url}?apikey=${encodeURIComponent(apiKey)}`, body);
        if (sent) return;
      } catch {
        // fall through to fetch
      }
    }

    try {
      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: apiKey,
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch {
      // best-effort tracking; ignore failures
    }
  };

  useEffect(() => {
    if (variant) {
      trackEvent("impression");
    }
  }, [variant]);

  return { variant, variantConfig, trackEvent, isLoading: !variant && !!test };
}
